/**
 * Page /verifier-carte/:token — Vérification PUBLIQUE d'une carte déchetterie.
 *
 * Ouverte en scannant le QR code de l'attestation (par un agent municipal).
 * Aucune authentification. Affiche le strict nécessaire : validité, numéro,
 * nom de l'adhérent, commune, dates. Ne révèle aucune autre donnée personnelle.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { Card, Badge, LogoMark, SectionEyebrow } from '@/components/ui';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Recycle, CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';

type Verif = {
  valide: boolean;
  statut: 'valide' | 'expiree' | 'revoquee' | 'introuvable';
  numeroCarte: string | null;
  nomAdherent: string | null;
  commune: string | null;
  emiseLe: string | null;
  expireLe: string | null;
};

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
function frDate(d: string | null): string {
  if (!d) return '—';
  const [y, m, day] = d.split('-').map(Number);
  return `${day} ${MOIS[(m ?? 1) - 1]} ${y}`;
}

export function VerifierCartePage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<Verif | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!token) { setError('Lien invalide.'); setLoading(false); return; }
      try {
        const r = await api.public.verifierCarte(token);
        if (alive) setData(r as Verif);
      } catch (e) {
        if (alive) setError(e instanceof ApiException ? e.message : 'Vérification impossible.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const config = (() => {
    if (!data) return null;
    switch (data.statut) {
      case 'valide': return { Icon: CheckCircle2, color: 'text-success', bg: 'border-success/40 bg-success-tint/20', titre: 'Carte valide' };
      case 'expiree': return { Icon: Clock, color: 'text-warning', bg: 'border-warning/40 bg-warning-tint/20', titre: 'Carte expirée' };
      case 'revoquee': return { Icon: XCircle, color: 'text-urgent', bg: 'border-urgent/40 bg-urgent-tint/20', titre: 'Carte révoquée' };
      default: return { Icon: AlertCircle, color: 'text-ink-muted', bg: 'border-border bg-surface/50', titre: 'Carte introuvable' };
    }
  })();

  return (
    <div className="min-h-screen bg-bg">
      <div className="absolute right-4 top-4"><ThemeToggle /></div>
      <main className="mx-auto flex max-w-md flex-col px-4 py-16">
        <div className="mb-6 flex items-center gap-3">
          <LogoMark icon={Recycle} accent="grc" size="lg" />
          <div>
            <SectionEyebrow as="div">Vérification de carte</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold text-ink">Carte déchetterie</h1>
          </div>
        </div>

        {loading ? (
          <Card><div className="p-6 text-center text-sm text-ink-muted">Vérification en cours…</div></Card>
        ) : error ? (
          <Card><div className="p-6 text-center text-sm text-urgent">{error}</div></Card>
        ) : data && config ? (
          <Card>
            <div className={`flex flex-col items-center gap-2 rounded-r-lg border ${config.bg} p-6 text-center`}>
              <config.Icon className={`h-12 w-12 ${config.color}`} aria-hidden="true" />
              <div className="text-lg font-semibold text-ink">{config.titre}</div>
              {data.statut === 'introuvable' && (
                <p className="text-sm text-ink-muted">Aucune carte ne correspond à ce code.</p>
              )}
            </div>

            {data.statut !== 'introuvable' && (
              <div className="mt-4 space-y-2 px-2 pb-2">
                {data.numeroCarte && (
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-subtle">Numéro</span>
                    <span className="font-semibold text-ink">{data.numeroCarte}</span>
                  </div>
                )}
                {data.nomAdherent && (
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-subtle">Adhérent</span>
                    <span className="font-medium text-ink">{data.nomAdherent}</span>
                  </div>
                )}
                {data.commune && (
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-subtle">Commune</span>
                    <span className="text-ink">{data.commune}</span>
                  </div>
                )}
                {data.emiseLe && (
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-subtle">Émise le</span>
                    <span className="text-ink">{frDate(data.emiseLe)}</span>
                  </div>
                )}
                {data.expireLe && (
                  <div className="flex justify-between text-sm">
                    <span className="text-ink-subtle">Valable jusqu'au</span>
                    <span className="text-ink">{frDate(data.expireLe)}</span>
                  </div>
                )}
                <div className="pt-2">
                  {data.valide
                    ? <Badge variant="success" size="sm">Autorisée à déposer</Badge>
                    : <Badge variant="urgent" size="sm">Non valide — refuser l'accès</Badge>}
                </div>
              </div>
            )}
          </Card>
        ) : null}

        <p className="mt-6 text-center text-xs text-ink-dim">
          Vérification officielle · Vision Pro Collectivité
        </p>
      </main>
    </div>
  );
}
